import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  FormControlLabel, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded';
import AppShell from '../components/AppShell';
import api from '../services/api';
import { partnerName, when } from './common';
import { ChooseViewsDialog, PartnerDialog, PasswordHelp } from './PartnerDialogs';

/** Every partner account (Stack only, 7k): who they are, the views they see, and their access. */
const PartnersPage = () => {
  const [partners, setPartners] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // 'new' | partner
  const [choosing, setChoosing] = useState(null); // partner whose views are being chosen
  const [help, setHelp] = useState(null); // {partner, help}
  const [confirm, setConfirm] = useState(null); // partner to deactivate
  const [regenerate, setRegenerate] = useState(false);
  const [pending, setPending] = useState(null);

  const load = useCallback(() => {
    api.get('/auth/partners/').then(({ data }) => setPartners(data)).catch(() => setError("Partners can't be loaded just now."));
  }, []);
  useEffect(() => { load(); }, [load]);

  const replace = (p) => setPartners((list) => (list.some((x) => x.id === p.id) ? list.map((x) => (x.id === p.id ? p : x)) : [p, ...list]));

  const setActive = async (partner, isActive, regenerateLinks = false) => {
    setPending(partner.id);
    setError(null);
    try {
      const body = { is_active: isActive, ...(regenerateLinks && { regenerate_view_links: true }) };
      const { data } = await api.patch(`/auth/partners/${partner.id}/`, body);
      replace(data);
    } catch (err) {
      setError(err?.response?.data?.error || "The account couldn't be changed.");
    } finally {
      setPending(null);
      setConfirm(null);
    }
  };

  const passwordLink = async (partner) => {
    setPending(partner.id);
    setError(null);
    try {
      const { data } = await api.post(`/auth/partners/${partner.id}/password-link/`);
      setHelp({ partner, help: data });
    } catch (err) {
      setError(err?.response?.data?.error || "A password link couldn't be made.");
    } finally {
      setPending(null);
    }
  };

  return (
    <AppShell>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <GroupRoundedIcon color="primary" />
        <Typography variant="h4" fontFamily="'Work Sans', sans-serif" fontWeight={700} sx={{ flex: 1 }}>Partners</Typography>
        <Button variant="contained" startIcon={<PersonAddRoundedIcon />} onClick={() => setEditing('new')}
          sx={{ textTransform: 'none', borderRadius: 2 }}>
          Add partner
        </Button>
      </Box>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Partner accounts sign in to see the quota views shared with them. Choose each partner&apos;s views here, or share a
        view with partners from its Share button in Quota Monitoring.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {!partners && !error && <CircularProgress size={24} />}
      {partners && (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                {['Name', 'Email', 'Company', 'Views they see', 'Last login', ''].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {partners.length === 0 && (
                <TableRow><TableCell colSpan={6} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>No partner accounts yet.</TableCell></TableRow>
              )}
              {partners.map((p) => (
                <TableRow key={p.id} sx={{ opacity: p.is_active ? 1 : 0.6 }}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{partnerName(p)}</Typography>
                    <Typography variant="caption" color="text.secondary">{p.username}</Typography>
                    {!p.is_active && <Chip size="small" label="Deactivated" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell>{p.email || '—'}</TableCell>
                  <TableCell>{p.company || '—'}</TableCell>
                  <TableCell>
                    {p.views.length === 0 ? <Typography variant="body2" color="text.secondary">None</Typography> : (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {p.views.map((v) => <Chip key={v.id} size="small" variant="outlined" label={v.name} title={v.survey} />)}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{when(p.last_login)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    <Button size="small" onClick={() => setChoosing(p)} disabled={pending === p.id} sx={{ textTransform: 'none' }}>Choose views</Button>
                    <Button size="small" onClick={() => setEditing(p)} disabled={pending === p.id} sx={{ textTransform: 'none' }}>Edit</Button>
                    {p.is_active && (
                      <Button size="small" onClick={() => passwordLink(p)} disabled={pending === p.id} sx={{ textTransform: 'none' }}>Password link</Button>
                    )}
                    {p.is_active ? (
                      <Button size="small" color="error" onClick={() => { setRegenerate(false); setConfirm(p); }} disabled={pending === p.id} sx={{ textTransform: 'none' }}>Deactivate</Button>
                    ) : (
                      <Button size="small" onClick={() => setActive(p, true)} disabled={pending === p.id} sx={{ textTransform: 'none' }}>Reactivate</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {editing && (
        <PartnerDialog partner={editing === 'new' ? null : editing} onClose={() => setEditing(null)}
          onSaved={(partner, passwordHelp) => {
            replace(partner);
            setEditing(null);
            if (passwordHelp) setHelp({ partner, help: passwordHelp });
          }} />
      )}
      {choosing && (
        <ChooseViewsDialog partner={choosing} onClose={() => setChoosing(null)}
          onSaved={(partner) => { replace(partner); setChoosing(null); }} />
      )}
      {help && <PasswordHelp help={help.help} partner={help.partner} onClose={() => setHelp(null)} />}
      <Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)}>
        <DialogTitle>Deactivate {confirm && partnerName(confirm)}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            They can&apos;t sign in until reactivated, and any password link they have stops working. The views shared with
            them stay listed, ready for reactivation.
          </DialogContentText>
          {confirm && confirm.views.length > 0 && (
            <>
              <Alert severity={regenerate ? 'info' : 'warning'} sx={{ mt: 2 }}>
                {regenerate
                  ? `The links of their ${confirm.views.length} view${confirm.views.length !== 1 ? 's' : ''} will be regenerated, so the old links stop working for everyone who has them. Send the new links to anyone else who should keep access.`
                  : `Otherwise the links of their ${confirm.views.length} view${confirm.views.length !== 1 ? 's' : ''} keep working: anyone with one of those links, including this partner, can still open the view without signing in.`}
              </Alert>
              <FormControlLabel sx={{ mt: 1 }}
                control={<Checkbox checked={regenerate} onChange={(e) => setRegenerate(e.target.checked)} />}
                label={`Also regenerate the links of the ${confirm.views.length} view${confirm.views.length !== 1 ? 's' : ''} this partner can see`} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => setActive(confirm, false, regenerate)} sx={{ textTransform: 'none' }}>
            {regenerate ? 'Deactivate and regenerate links' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>
    </AppShell>
  );
};

export default PartnersPage;
